import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Shield, AlertCircle, Users } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ADMIN_EMAILS, isMasterAdmin } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

/**
 * F8 — Admin page no longer ships fake user data.
 *
 * The previous build seeded a hardcoded fake-user array (fabricated
 * sign-in timestamps, fake verification status) which gave the impression
 * of a working admin console. That's a production-integrity bug — admins
 * could not tell real users from fake ones.
 *
 * Until a real ``/api/admin/users`` endpoint exists, this page only shows
 * the static, configured admin roster (`ADMIN_EMAILS` from
 * ``lib/supabase``) and a clear disclosure that full user management is
 * not yet available.
 */
export default function AdminPage() {
  const { user, isAdminUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAdminUser) {
      navigate('/dashboard');
    }
  }, [isAdminUser, navigate]);

  if (!isAdminUser) return null;

  const adminRows = ADMIN_EMAILS || [];

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto" data-testid="admin-page">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Team Management</h1>
          <p className="text-muted-foreground">
            Configured administrator roster. Live user management is not yet wired to the backend.
          </p>
        </div>

        {isMasterAdmin(user?.email) && (
          <Alert className="mb-6 border-accent" data-testid="master-admin-alert">
            <Shield className="h-4 w-4 text-accent" />
            <AlertDescription>
              You are logged in as <strong>Master Administrator</strong>.
            </AlertDescription>
          </Alert>
        )}

        {/* F8 — Honest empty-state disclosure. NEVER fake users. */}
        <Alert
          className="mb-6 border-[hsl(var(--warning))]"
          data-testid="admin-not-available-banner"
        >
          <AlertCircle className="h-4 w-4 text-[hsl(var(--warning))]" />
          <AlertDescription>
            <strong>Admin user management is not available yet.</strong>
            <br />
            This view shows only the static admin roster configured in
            <code className="mx-1">lib/supabase.ADMIN_EMAILS</code>. Real
            user invitations, verification, and sign-in history will appear
            here once the backend admin API is built. Until then, manage
            users directly in the Supabase dashboard.
          </AlertDescription>
        </Alert>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" /> Configured Administrators
            </CardTitle>
            <CardDescription>
              Static roster from <code>ADMIN_EMAILS</code>. Not fetched from the database.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {adminRows.length === 0 ? (
              <p
                className="text-center text-muted-foreground py-6"
                data-testid="admin-empty-state"
              >
                No administrators configured.
              </p>
            ) : (
              <ul className="divide-y divide-border" data-testid="admin-list">
                {adminRows.map((a) => (
                  <li
                    key={a.email}
                    className="flex items-center justify-between py-3"
                    data-testid={`admin-row-${a.email}`}
                  >
                    <span className="font-medium">{a.email}</span>
                    {a.role === 'master_admin' ? (
                      <Badge className="bg-accent text-accent-foreground">
                        <Shield className="h-3 w-3 mr-1" /> Master Admin
                      </Badge>
                    ) : (
                      <Badge className="bg-accent/80 text-accent-foreground">Admin</Badge>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
